/// <amd-module name="Artifacts/Connect/hooks/Tools" />
import { IToolTabCell } from "Artifacts/Connect/components/ConnectToFeed";
import { ToolService } from "Artifacts/Connect/services/ToolService";
import { ICombinedFeedView } from "Artifacts/Connect/types/ICombinedFeedView";
import { ITool } from "Artifacts/Connect/types/ITool";
import { CiConstants } from "Artifacts/Root/Constants";
import { CustomerIntelligenceHelper } from "Artifacts/Root/helpers/CustomerIntelligenceHelper";
import { getIsUserMember } from "Artifacts/Root/helpers/UserHelpers";
import { IVssPageContext } from "VSS/Platform/Context";
import { ArrayItemProvider, IItemProvider } from "VSSUI/Utilities/Provider";
import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * useConnectToFeedList encapsulates tool list state management
 * and provides API for selecting tools.
 * @param pageContext VSS page context.
 * @returns Object with tool list state and API callbacks.
 */
export function useConnectToFeedList(
  pageContext: IVssPageContext,
  feed: ICombinedFeedView | undefined,
): {
  toolsItemProvider: IItemProvider<IToolTabCell>;
  protocolNames: string[];
  selectedTool: ITool | undefined;
  selectTool: (toolId?: string) => void;
} {
  const tools = useAvailableTools(pageContext, feed);
  const [protocolNames, itemProvider] = useMemo(() => getListData(tools), [tools]);
  const [selectedTool, setSelectedTool] = useState<ITool | undefined>(undefined);

  const selectTool = useCallback(
    (toolId?: string) => {
      const selectedTool = itemProvider.value.find((value) => value.tool?.name === toolId)?.tool;
      setSelectedTool(selectedTool);
      CustomerIntelligenceHelper.publishEvent(pageContext, CiConstants.ConnectToFeedToolChange, {
        ["ToolName"]: selectedTool?.name,
      });

      // Note: We can use history.replace to change the browser URL when switching tabs
      // but it may be a11y non-compliant. It will also cause the page to be stuck in a loading state
      // so more work is needed to make this work.
    },
    [itemProvider, pageContext],
  );

  return {
    toolsItemProvider: itemProvider,
    protocolNames: protocolNames,
    selectedTool: selectedTool,
    selectTool: selectTool,
  };
}

/**
 * useAvailableTools encapsulates state and retrieval of tools from ToolService.
 * @param pageContext VSS context.
 * @returns Array of tools.
 */
export function useAvailableTools(
  pageContext: IVssPageContext,
  feed: ICombinedFeedView | undefined,
): ITool[] {
  const [tools, setTools] = useState<ITool[]>([]);
  const userIsMember = useMemo(() => getIsUserMember(pageContext), [pageContext]);

  useEffect(() => {
    if (feed) {
      const service = new ToolService(pageContext);
      setTools(service.getAvailableTools(pageContext, feed, userIsMember));
    }
  }, [pageContext, feed, userIsMember]);

  return tools;
}

/**
 * getListData prepares selection and item provider objects for tool list.
 * NOTE: this function should be pure, because it is used in the hook with memoization.
 * @param tools List of tools.
 * @returns An array of protocol names and item provider with tool list.
 */
function getListData(tools: ITool[]): [string[], IItemProvider<IToolTabCell>] {
  const toolsByProtocol = new Map<string, ITool[]>();
  const cells: IToolTabCell[] = [];
  tools.forEach((tool) => {
    // First split the tools by protocol
    const toolSet = toolsByProtocol.get(tool.protocol.name);
    if (toolSet) {
      toolSet.push(tool);
    } else {
      toolsByProtocol.set(tool.protocol.name, [tool]);
    }
    // Add the tool into the list
    cells.push({ name: tool.name, id: tool.name, tool });
  });

  return [[...toolsByProtocol.keys()], new ArrayItemProvider(cells)];
}
